import { flow, types, getEnv, getRoot } from 'mobx-state-tree'
import { ROLES } from 'constants/account.roles'
import { STORAGE_KEYS } from 'constants/storage.keys'

import { Token } from './token'
import { User } from '../member/user'
import { Beneficiary } from '../member/beneficiary'
import { Sponsor } from '../member/sponsor'

export const Auth = types
  .model('Auth', {
    isSigningIn: types.optional(types.boolean, false),
    entity: types.maybe(types.union(User, Beneficiary, Sponsor)),
    token: types.maybe(Token),
    refreshToken: types.maybe(Token),
  })
  .actions((self) => {
    const { services, storage } = getEnv(self)
    const {
      authService,
      memberService,
      registerService,
      disposableTokensService,
    } = services

    return {
      exchangeToken: flow(function* (token, nonce) {
        const userEntity = yield disposableTokensService.getToken(token, nonce)
        yield self.setToken(userEntity)
        self.entity = yield memberService.getCurrentProfile()
      }),
      invalidate() {
        self.entity = undefined
        self.token = undefined
        self.refreshToken = undefined
      },

      tryRefreshToken: flow(function* tryRefreshToken() {
        try {
          const entitiy = yield authService.refreshToken(self.refreshToken.raw)

          self.refreshToken = entitiy.refreshToken
          self.token = entitiy.token

          storage.setItem(STORAGE_KEYS.TOKEN, entitiy.token)
          storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, entitiy.refreshToken)

          return true
        } catch (error) {
          // If refresh fails, clear invalid tokens
          self.invalidate()
          storage.removeItem(STORAGE_KEYS.TOKEN)
          storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
          return false
        }
      }),

      revalidateLogin: flow(function* revalidateLogin() {
        const token = storage.getItem(STORAGE_KEYS.TOKEN)
        const refreshToken = storage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

        if (!(token && refreshToken)) {
          self.signout()
          return
        }

        self.refreshToken = refreshToken
        self.token = token

        // Check if token is expired before trying to get profile
        if (self.token.isExpired) {
          try {
            // Try to refresh the token
            const isRefreshed = yield self.tryRefreshToken()
            if (!isRefreshed) {
              // If refresh fails, sign out silently to let layout render
              self.signout()
              return
            }
          } catch (error) {
            // If refresh fails, sign out silently to let layout render
            self.signout()
            return
          }
        }

        try {
          self.entity = yield memberService.getCurrentProfile()
        } catch (error) {
          // If getting profile fails, sign out silently to let layout render
          self.signout()
        }
      }),

      login: flow(function* login(email, password) {
        const entitiy = yield authService.login(email, password)

        self.refreshToken = entitiy.refreshToken
        self.token = entitiy.token

        storage.setItem(STORAGE_KEYS.TOKEN, entitiy.token)
        storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, entitiy.refreshToken)

        const entityProfile = yield memberService.getCurrentProfile()
        self.entity = {
          id: self.token.relatedEntityId,
          ...entitiy,
        }
        self.entity = {
          ...self.entity,
          address: {
            ...(entityProfile?.address || {}),
          },
        }
      }),

      // mobile flow
      setToken(userEntity) {
        self.refreshToken = userEntity.refreshToken
        self.token = userEntity.token
        // tmp
        self.entity = {
          id: self.token.relatedEntityId,
          ...userEntity,
        }

        storage.setItem(STORAGE_KEYS.TOKEN, userEntity.token)
        storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, userEntity.refreshToken)
      },

      register: flow(function* register({ type, entity }) {
        const { confirmation, ...params } = entity
        const registrationPromise =
          ROLES.BENEFICIARY === type
            ? registerService.registerBeneficiary(params)
            : registerService.registerUser(params)

        const entitiy = yield registrationPromise

        self.refreshToken = entitiy.refreshToken
        self.token = entitiy.token

        storage.setItem(STORAGE_KEYS.TOKEN, entitiy.token)
        storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, entitiy.refreshToken)

        const entityProfile = yield memberService.getCurrentProfile()
        self.entity = {
          id: self.token.relatedEntityId,
          ...entitiy,
        }
        self.entity = {
          ...self.entity,
          address: {
            ...(entityProfile?.address || {}),
          },
        }
      }),

      resetPassword: flow(function* resetPassword(email, options = {}) {
        yield authService.resetPassword(email, options)
      }),

      confirmPassword: flow(function* confirmPassword(token, email, password) {
        yield authService.confirmPassword(token, email, password)
      }),

      signout() {
        const root = getRoot(self)
        // Clear auth-related storage items specifically
        storage.removeItem(STORAGE_KEYS.TOKEN)
        storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        self.invalidate()
        root.resetStore()
        authService.logOut()
      },
    }
  })
  .views((self) => ({
    get isSignedIn() {
      const signedIn = Boolean(self.token && self.entity)
      return !self.isSigningIn && signedIn
    },

    get loggedEntity() {
      return self.entity
    },
  }))
